import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Chip,
    Grid,
    Paper,
    Avatar,
    IconButton,
    Button,
    Autocomplete,
    FormControlLabel,
    Switch,
    Checkbox,
    CircularProgress,
    Tooltip,
    ListItemButton,
    ListItemText
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { useFormik } from "formik";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearCreateFarmerCommitmentAction, clearUpdateFarmerCommitmentAction, createFarmerCommitmentAction, getAllDistrictsAction, getAllFarmerCommitmentsAction, getAllMandalsAction, getAllVillagesAction, getAllCropsAction, updateFarmerCommitmentAction } from "../../../store/actions";
import toast from "react-hot-toast";

/* ── Search-or-Create Location Input ── */
const LocationSearchInput = ({
    label, value, selectedId, suggestions,
    onTextChange, onSelect, disabled, error, helperText,
}) => {
    const [focused, setFocused] = useState(false);

    const filtered = useMemo(() => {
        if (!value?.trim()) return [];
        return suggestions.filter((s) =>
            s.label.toLowerCase().includes(value.toLowerCase().trim())
        );
    }, [value, suggestions]);

    const showSuggestions = focused && value?.trim().length > 0 && filtered.length > 0;
    const isNew = value?.trim().length > 0 && !selectedId;

    let displayHelper = "";
    let helperColor;
    if (error && helperText) {
        displayHelper = helperText;
        helperColor = "error.main";
    } else if (selectedId) {
        displayHelper = `✓ Existing ${label.toLowerCase()} selected`;
        helperColor = "success.main";
    } else if (isNew) {
        displayHelper = `New ${label.toLowerCase()} will be created`;
        helperColor = "info.main";
    }

    return (
        <Box sx={{ position: "relative" }}>
            <TextField
                fullWidth
                size="small"
                label={label}
                value={value}
                onChange={(e) => onTextChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 200)}
                disabled={disabled}
                error={error}
                helperText={displayHelper}
                placeholder={`Search or create ${label.toLowerCase()}`}
                FormHelperTextProps={{ sx: { color: helperColor } }}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#FAFAFA",
                    },
                }}
            />
            {showSuggestions && (
                <Paper
                    elevation={4}
                    sx={{
                        position: "absolute",
                        zIndex: 1300,
                        width: "100%",
                        maxHeight: 200,
                        overflow: "auto",
                        mt: 0.5,
                        borderRadius: 1,
                    }}
                >
                    {filtered.map((item) => (
                        <ListItemButton
                            key={item.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                onSelect(item);
                                setFocused(false);
                            }}
                            sx={{ py: 0.5, px: 1.5 }}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{ variant: "body2" }}
                            />
                        </ListItemButton>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

const FarmerRegister = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [majorCropInput, setMajorCropInput] = useState("");
    const [sameWhatsapp, setSameWhatsapp] = useState(false);

    // --- Geo location ---
    const [geoLocating, setGeoLocating] = useState(false);

    const id = location?.state?.farmerId;
    const Edit = location?.state?.mode === 'edit';
    const View = location?.state?.mode === 'view';

    const userDetails = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userDetails"));
        } catch {
            return null;
        }
    }, []);
    const userId = userDetails?.id;
    const userRole = userDetails?.role;

    // Lookup lists
    const { successData: districts } = useSelector(state => state.getAllDistrictsReducer);
    const { successData: mandals } = useSelector(state => state.getAllMandalsReducer);

    const districtList = useMemo(() => {
        if (userRole === "SO" && Array.isArray(districts?.data?.districts)) {
            return districts.data.districts.filter(
                d => d.inchargeUser && d.inchargeUser.userId === userId
            );
        }
        if (userRole === "MDO" && Array.isArray(districts?.data?.districts) && Array.isArray(mandals?.data?.mandals)) {
            const mdoMandalDistrictIds = new Set(mandals.data.mandals
                .filter(m => m.inchargeUserId === userId)
                .map(m => m.districtId));
            return districts.data.districts.filter(d => mdoMandalDistrictIds.has(d.districtId));
        }
        return districts?.data?.districts || [];
    }, [districts, mandals, userId, userRole]);

    const mandalList = useMemo(() => {
        if (userRole === "MDO" && Array.isArray(mandals?.data?.mandals)) {
            return mandals.data.mandals.filter(m => m.inchargeUserId === userId);
        }
        return mandals?.data?.mandals || [];
    }, [mandals, userId, userRole]);

    const { successData: villages } = useSelector(state => state.getAllVillagesReducer);
    const villageList = useMemo(() => villages?.data?.villages || [], [villages]);

    const { successData: cropsData } = useSelector(state => state.getAllCropsReducer);

    const { successData: createFarmerSuccess, error, errorInfo } = useSelector(state => state.createFarmerCommitmentReducer);
    const { successData: farmerCommitmentData } = useSelector(state => state.getAllFarmerCommitmentsReducer);
    const { successData: updateFarmerSuccess, error: updateFarmerError, errorInfo: updateFarmerErrorInfo } = useSelector(state => state.updateFarmerCommitmentReducer);


    // Add local input state for major crop entry

    const normalizeCropName = (value) => value.trim().replace(/\s+/g, " ");

    const mergeMajorCropInput = (majorCrops, inputValue) => {
        const normalizedValue = normalizeCropName(inputValue);

        if (!normalizedValue) {
            return majorCrops;
        }

        const alreadyExists = majorCrops.some(
            (crop) => normalizeCropName(crop).toLowerCase() === normalizedValue.toLowerCase()
        );

        return alreadyExists ? majorCrops : [...majorCrops, normalizedValue];
    };

    const registerFarmerValidationSchema = yup.object({
        farmerName: yup.string().trim().required("Farmer name is required"),
        mobileNumber: yup
            .string()
            .matches(/^\d{10}$/, "Enter 10 digit mobile number")
            .required("Mobile number is required"),
        whatsappNumber: yup
            .string()
            .matches(/^\d{10}$/, "Enter 10 digit WhatsApp number")
            .nullable(),
        districtName: yup.string().required("District is required"),
        districtId: yup.string().optional(),
        mandalName: yup.string().required("Mandal is required"),
        mandalId: yup.string().optional(),
        villageName: yup.string().required("Village is required"),
        villageId: yup.string().optional(),
        geoCode: yup.string().nullable(),
        totalAcres: yup
            .number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .nullable()
            .typeError("Total acres must be a number"),
        irrigationType: yup.string().nullable(),
        irrigationAcres: yup
            .number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .nullable()
            .typeError("Irrigation acres must be a number"),
        majorCrops: yup.array().of(yup.string().trim()).default([]),
    });

    const data = farmerCommitmentData?.data;

    const initialValues = useMemo(() => {
        let majorCropsArr = [];
        if (Array.isArray(data?.majorCrops)) {
            majorCropsArr = data.majorCrops;
        } else if (typeof data?.majorCrops === "string" && data.majorCrops.length) {
            majorCropsArr = data.majorCrops.split(",").map(s => s.trim()).filter(Boolean);
        }
        const distList = districts?.data?.districts || [];
        const mandList = mandals?.data?.mandals || [];
        const villList = villages?.data?.villages || [];
        return {
            farmerName: data?.name ?? "",
            mobileNumber: data?.mobile ?? "",
            whatsappNumber: data?.whatsappNo ?? "",
            districtId: data?.districtId ?? "",
            districtName: distList.find(d => d.districtId === data?.districtId)?.name ?? "",
            mandalId: data?.mandalId ?? "",
            mandalName: mandList.find(m => m.mandalId === data?.mandalId)?.name ?? "",
            villageId: data?.villageId ?? "",
            villageName: villList.find(v => v.villageId === data?.villageId)?.name ?? "",
            geoCode: data?.geoCode ?? "",
            totalAcres: data?.totalAcres ?? "",
            irrigationAcres: data?.acresUnderIrrigation ?? "",
            focusCrop: data?.focusCrop ?? "",
            irrigationType: data?.irrigationType ?? "",
            existingUser: !!data?.existingUser,
            majorCrops: majorCropsArr,
        };
}, [data, districts, mandals, villages]);

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: registerFarmerValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            const toNumber = (v) =>
                v === "" || v === null || v === undefined ? undefined : Number(v);

            // existing → send ID; new text → send name string (backend creates it)
            const districtValue = values.districtId ? values.districtId : values.districtName.trim();
            const mandalValue   = values.mandalId   ? values.mandalId   : values.mandalName.trim();
            const villageValue  = values.villageId  ? values.villageId  : values.villageName.trim();
            const finalMajorCrops = mergeMajorCropInput(values.majorCrops, majorCropInput);

            const payload = {
                name: values.farmerName,
                mobile: values.mobileNumber,
                whatsappNo: values.whatsappNumber,
                districtId: districtValue,
                mandalId: mandalValue,
                villageId: villageValue,
                incharge_user_id: userId,
                geoCode: values.geoCode,
                totalAcres: toNumber(values.totalAcres),
                focusCrop: values.focusCrop,
                irrigationType: values.irrigationType,
                acresUnderIrrigation: values.irrigationAcres === undefined || values.irrigationAcres === null ? "" : String(values.irrigationAcres),
                majorCrops: finalMajorCrops,
                existingUser: values.existingUser,
            };
            if (Edit && id) {
                dispatch(updateFarmerCommitmentAction({
                    endPoint: `farmer/${id}`,
                    ...payload
                }));
            } else {
                dispatch(createFarmerCommitmentAction(payload));
            }

            if (majorCropInput.trim()) {
                formik.setFieldValue("majorCrops", finalMajorCrops, false);
                setMajorCropInput("");
            }
        }
    });

    const commitMajorCropInput = () => {
        if (View) return;

        const nextMajorCrops = mergeMajorCropInput(formik.values.majorCrops, majorCropInput);

        formik.setFieldTouched("majorCrops", true, false);

        if (nextMajorCrops !== formik.values.majorCrops) {
            formik.setFieldValue("majorCrops", nextMajorCrops);
        }

        if (majorCropInput.trim()) {
            setMajorCropInput("");
        }
    };

    // Destructure setFieldValue from formik
    const { setFieldValue } = formik;

    useEffect(() => {
        if (sameWhatsapp) {
            setFieldValue("whatsappNumber", formik.values.mobileNumber);
        }
    }, [sameWhatsapp, formik.values.mobileNumber, setFieldValue]);

    /* Normalised suggestion lists for LocationSearchInput */
    const districtSuggestions = useMemo(() =>
        districtList.map(d => ({ id: d.districtId, label: d.name || "" })).filter(d => d.id && d.label),
        [districtList]
    );

    const mandalSuggestions = useMemo(() => {
        if (!formik.values.districtId) return [];
        return mandalList
            .filter(m => String(m.districtId) === String(formik.values.districtId))
            .map(m => ({ id: m.mandalId, label: m.name || "" }))
            .filter(m => m.id && m.label);
    }, [mandalList, formik.values.districtId]);

    const villageSuggestions = useMemo(() => {
        if (!formik.values.mandalId) return [];
        return villageList
            .filter(v => String(v.mandalId) === String(formik.values.mandalId))
            .map(v => ({ id: v.villageId, label: v.name || "" }))
            .filter(v => v.id && v.label);
    }, [villageList, formik.values.mandalId]);

    useEffect(() => {
        dispatch(getAllDistrictsAction());
        dispatch(getAllMandalsAction());
        dispatch(getAllVillagesAction());
        dispatch(getAllCropsAction());
        if (Edit || View) dispatch(getAllFarmerCommitmentsAction({
            endPoint: `farmer/${id}`,
        }));
    }, [dispatch, Edit, View, id]);

    // Auto-fill district when only one option is available for the user
    useEffect(() => {
        if (districtSuggestions.length === 1 && !formik.values.districtId && !Edit && !View) {
            const only = districtSuggestions[0];
            formik.setFieldValue("districtName", only.label);
            formik.setFieldValue("districtId", only.id);
        }
    }, [districtSuggestions]);

    useEffect(() => {
        if (createFarmerSuccess) {
            const farmerId = createFarmerSuccess?.data?.farmerId;
            toast.success("Farmer registered successfully");
            navigate('/SOorMDO/farmerCommitment', {
                state: {
                    farmerId: farmerId,
                    openCommitmentDialog: true
                }
            });
        }
        if (error || errorInfo) {
            toast.error(errorInfo || errorInfo?.message || "Failed to register farmer");
        }
        return () => {
            dispatch(clearCreateFarmerCommitmentAction())
        }
    }, [createFarmerSuccess, dispatch, navigate, error, errorInfo]);

    useEffect(() => {
        if (updateFarmerSuccess) {
            toast.success("Farmer updated successfully");
            navigate('/SOorMDO/farmerCommitment');
        }
        if (updateFarmerError || updateFarmerErrorInfo) {
            toast.error(updateFarmerErrorInfo || updateFarmerErrorInfo?.message || "Failed to update farmer");
        }
        return () => {
            dispatch(clearUpdateFarmerCommitmentAction())
        }
    }, [updateFarmerSuccess, dispatch, navigate, updateFarmerError, updateFarmerErrorInfo]);

    return (
        <Paper
            elevation={0}
            sx={{
                mx: "auto",
                p: { xs: 2, sm: 3, md: 4 }, 
                mt: { xs: 7, sm: 0, md: 0 },
                borderRadius: 3,
                position: "relative"
            }}
        >
            <IconButton
                aria-label="Close"
                onClick={() => navigate(-1)}
                sx={{
                    position: "absolute",
                    top: { xs: 2, sm: 10, md: 4 },
                    right: { xs: 2, sm: 10, md: 4 },
                    bgcolor: "#F3F4F6",
                    "&:hover": { bgcolor: "#E5E7EB" }
                }}
                size="small"
            >
                <CloseIcon fontSize="small" />
            </IconButton>
            {/* Header */}
            <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }} // 🔹 stack on mobile
                gap={2}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                mt={{ xs: 0, sm: 4, md: 0 }}
            >
                <Box display="flex" gap={1.5}>
                    <Avatar sx={{ bgcolor: "black", width: 32, height: 32 }}>
                        <CheckCircleIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography fontWeight={600}>
                            Farmer Information Management
                        </Typography>
                        <Typography fontSize={14} color="text.secondary">
                            Register and manage detailed agricultural profiles for farmers.
                        </Typography>
                    </Box>
                </Box>

                <Chip
                    label="System Active"
                    sx={{
                        bgcolor: "#D1FAE5",
                        color: "#065F46",
                        width: "fit-content"
                    }}
                />
            </Box>

            <form onSubmit={formik.handleSubmit} noValidate>
                {/* Personal Information */}
                <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
                    <PersonIcon fontSize="small" />
                    <Typography fontWeight={600}>Personal Information</Typography>
                </Box>


                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Farmer Name
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            name="farmerName"
                            value={formik.values.farmerName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter farmer name"
                            error={formik.touched.farmerName && Boolean(formik.errors.farmerName)}
                            helperText={formik.touched.farmerName && formik.errors.farmerName}
                            disabled={View}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#FAFAFA"
                                }
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Mobile Number
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            name="mobileNumber"
                            type="text"
                            value={formik.values.mobileNumber}
                            onChange={e => {
                                // Only allow numbers, max 10 digits
                                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                formik.setFieldValue("mobileNumber", val);
                                if (sameWhatsapp) {
                                    formik.setFieldValue("whatsappNumber", val);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                            placeholder="Enter mobile number"
                            error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                            helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                            disabled={View}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#FAFAFA"
                                }
                            }}
                        />
                    </Grid>
                    {/* Checkbox between Mobile and WhatsApp fields */}
                    <Grid item size={{ xs: 12, sm: 6, md: 1 }} display="flex" alignItems="center" justifyContent="center" ml={{ xs: -10, sm: -10, md: 2 }} mt={0} mr={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sameWhatsapp}
                                    onChange={e => setSameWhatsapp(e.target.checked)}
                                    color="primary"
                                    disabled={View}
                                />
                            }
                            label={<Typography fontSize={12} display="inline">same as mobile</Typography>}
                            sx={{ whiteSpace: 'nowrap', m: 0 }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            WhatsApp Number
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            name="whatsappNumber"
                            type="text"
                            value={formik.values.whatsappNumber}
                            onChange={e => {
                                if (!sameWhatsapp) {
                                    // Only allow numbers, max 10 digits
                                    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                    formik.setFieldValue("whatsappNumber", val);
                                }
                            }}
                            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                            onBlur={formik.handleBlur}
                            placeholder="Enter WhatsApp number"
                            error={formik.touched.whatsappNumber && Boolean(formik.errors.whatsappNumber)}
                            helperText={formik.touched.whatsappNumber && formik.errors.whatsappNumber}
                            disabled={View || sameWhatsapp}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#FAFAFA"
                                }
                            }}
                        />
                        
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Existing User
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(formik.values.existingUser)}
                                    onChange={(e) => formik.setFieldValue("existingUser", e.target.checked)}
                                    name="existingUser"
                                    color="primary"
                                    disabled={View}
                                />
                            }
                            label={formik.values.existingUser ? "Yes" : "No"}
                        />
                    </Grid>
                </Grid>

                {/* Location Details */}
                <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
                    <LocationOnIcon fontSize="small" />
                    <Typography fontWeight={600}>Location Details</Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Autocomplete
                            options={districtSuggestions}
                            value={districtSuggestions.find(d => d.id === formik.values.districtId) || null}
                            onChange={(_, newValue) => {
                                formik.setFieldValue("districtName", newValue?.label || "");
                                formik.setFieldValue("districtId", newValue?.id || "");
                                formik.setFieldTouched("districtName", true, false);
                                formik.setFieldValue("mandalName", "");
                                formik.setFieldValue("mandalId", "");
                                formik.setFieldValue("villageName", "");
                                formik.setFieldValue("villageId", "");
                            }}
                            disabled={View || districtSuggestions.length === 1}
                            getOptionLabel={(option) => option.label || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="District"
                                    placeholder="Select district"
                                    error={formik.touched.districtName && Boolean(formik.errors.districtName)}
                                    helperText={formik.touched.districtName && formik.errors.districtName}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: districtSuggestions.length === 1 ? "#F3F4F6" : "#FAFAFA",
                                        },
                                        '& .MuiInputBase-input.Mui-disabled': {
                                            WebkitTextFillColor: '#111827',
                                            fontWeight: 400,
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <LocationSearchInput
                            label="Mandal"
                            value={formik.values.mandalName}
                            selectedId={formik.values.mandalId}
                            suggestions={mandalSuggestions}
                            disabled={View || !formik.values.districtName?.trim()}
                            error={formik.touched.mandalName && Boolean(formik.errors.mandalName)}
                            helperText={formik.touched.mandalName && formik.errors.mandalName}
                            onTextChange={(text) => {
                                formik.setFieldValue("mandalName", text);
                                formik.setFieldTouched("mandalName", true, false);
                                // Normalize: lowercase, remove all spaces
                                const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                                const inputNorm = normalize(text);
                                const mandalMatch = mandalSuggestions.find(
                                    m => normalize(m.label) === inputNorm
                                );
                                formik.setFieldValue("mandalId", mandalMatch ? mandalMatch.id : "");
                                formik.setFieldValue("villageName", "");
                                formik.setFieldValue("villageId", "");
                            }}
                            onSelect={(item) => {
                                formik.setFieldValue("mandalName", item.label);
                                formik.setFieldValue("mandalId", item.id);
                                formik.setFieldTouched("mandalName", true, false);
                                formik.setFieldValue("villageName", "");
                                formik.setFieldValue("villageId", "");
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <LocationSearchInput
                            label="Village"
                            value={formik.values.villageName}
                            selectedId={formik.values.villageId}
                            suggestions={villageSuggestions}
                            disabled={View || !formik.values.mandalName?.trim()}
                            error={formik.touched.villageName && Boolean(formik.errors.villageName)}
                            helperText={formik.touched.villageName && formik.errors.villageName}
                            onTextChange={(text) => {
                                formik.setFieldValue("villageName", text);
                                formik.setFieldTouched("villageName", true, false);
                                // Normalize: lowercase, remove all spaces
                                const normalize = (str) => (str || '').toLowerCase().replace(/\s+/g, '');
                                const inputNorm = normalize(text);
                                const villageMatch = villageSuggestions.find(
                                    v => normalize(v.label) === inputNorm
                                );
                                formik.setFieldValue("villageId", villageMatch ? villageMatch.id : "");
                            }}
                            onSelect={(item) => {
                                formik.setFieldValue("villageName", item.label);
                                formik.setFieldValue("villageId", item.id);
                                formik.setFieldTouched("villageName", true, false);
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>

                        <Box display="flex" alignItems="center" gap={1}>
                            <Box
                                sx={{
                                    flex: 1,
                                    px: 0,
                                    py: 0.9,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: formik.touched.geoCode && formik.errors.geoCode ? "error.main" : "#E0E0E0",
                                    minHeight: 25,
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <Typography
                                    fontSize={13}
                                    color={formik.values.geoCode ? "text.primary" : "text.disabled"}
                                    noWrap
                                >
                                    {formik.values.geoCode || "Auto-filled on get location"}
                                </Typography>
                            </Box>
                            {!View && (
                                <Tooltip title="Get current location">
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={geoLocating}
                                            onClick={() => {
                                                if (!navigator.geolocation) {
                                                    toast.error("Geolocation is not supported by your browser");
                                                    return;
                                                }
                                                setGeoLocating(true);
                                                navigator.geolocation.getCurrentPosition(
                                                    (position) => {
                                                        const lat = position.coords.latitude.toFixed(6);
                                                        const lon = position.coords.longitude.toFixed(6);
                                                        formik.setFieldValue("geoCode", `${lat},${lon}`);
                                                        setGeoLocating(false);
                                                    },
                                                    (err) => {
                                                        toast.error("Unable to retrieve location: " + err.message);
                                                        setGeoLocating(false);
                                                    },
                                                    { enableHighAccuracy: true, timeout: 10000 }
                                                );
                                            }}
                                        >
                                            {geoLocating
                                                ? <CircularProgress size={18} />
                                                : <MyLocationIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                        {formik.touched.geoCode && formik.errors.geoCode && (
                            <Typography fontSize={11} color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                {formik.errors.geoCode}
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                {/* Agriculture Profile */}
                <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
                    <AgricultureIcon fontSize="small" />
                    <Typography fontWeight={600}>Agriculture Profile</Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 6, }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Major Crops
                        </Typography>
                        <Autocomplete
                            multiple
                            options={cropsData?.data?.crops?.map(c => c.name) || []}
                            value={formik.values.majorCrops || []}
                            onChange={(_, newValue) => {
                                formik.setFieldValue("majorCrops", newValue || []);
                                formik.setFieldTouched("majorCrops", true, false);
                            }}
                            onBlur={() => formik.setFieldTouched("majorCrops", true)}
                            filterSelectedOptions
                            renderTags={() => null}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Search and select crops"
                                    error={formik.touched.majorCrops && Boolean(formik.errors.majorCrops)}
                                    helperText={formik.touched.majorCrops && formik.errors.majorCrops}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "#FAFAFA"
                                        }
                                    }}
                                />
                            )}
                            disabled={View}
                        />
                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                            {(formik.values.majorCrops || []).map((option, idx) => (
                                <Chip
                                    key={option}
                                    label={option}
                                    onDelete={() => {
                                        if (View) return;
                                        const next = (formik.values.majorCrops || []).filter((c, i) => i !== idx);
                                        formik.setFieldValue("majorCrops", next);
                                    }}
                                    size="small"
                                    sx={{ mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Irrigation Type
                        </Typography>
                        <Autocomplete
                            options={["rainfed", "irrigation"]}
                            value={formik.values.irrigationType || null}
                            onChange={(_, newValue) => formik.setFieldValue("irrigationType", newValue || "")}
                            disabled={View}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select irrigation type"
                                    error={formik.touched.irrigationType && Boolean(formik.errors.irrigationType)}
                                    helperText={formik.touched.irrigationType && formik.errors.irrigationType}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Focus Crop
                        </Typography>
                        <Autocomplete
                            options={cropsData?.data?.crops?.map(c => c.name) || []}
                            value={formik.values.focusCrop || null}
                            onChange={(_, newValue) => {
                                formik.setFieldValue("focusCrop", newValue || "");
                                formik.setFieldTouched("focusCrop", true, false);
                            }}
                            onBlur={() => formik.setFieldTouched("focusCrop", true)}
                            getOptionLabel={(option) => option || ""}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Search and select focus crop"
                                    error={formik.touched.focusCrop && Boolean(formik.errors.focusCrop)}
                                    helperText={formik.touched.focusCrop && formik.errors.focusCrop}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "#FAFAFA"
                                        }
                                    }}
                                />
                            )}
                            disabled={View}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Total Acres
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            name="totalAcres"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min={0}
                            value={formik.values.totalAcres}
                            disabled={View}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Total acres"
                            error={formik.touched.totalAcres && Boolean(formik.errors.totalAcres)}
                            helperText={formik.touched.totalAcres && formik.errors.totalAcres}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*',
                                style: {
                                    MozAppearance: 'textfield',
                                    appearance: 'textfield',
                                }
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#FAFAFA"
                                },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                    appearance: 'textfield',
                                },
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6, }}>
                        <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
                            Irrigation Acres
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            name="irrigationAcres"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min={0}
                            value={formik.values.irrigationAcres}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={View}
                            placeholder="Acres under irrigation"
                            error={formik.touched.irrigationAcres && Boolean(formik.errors.irrigationAcres)}
                            helperText={formik.touched.irrigationAcres && formik.errors.irrigationAcres}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*',
                                style: {
                                    MozAppearance: 'textfield',
                                    appearance: 'textfield',
                                }
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#FAFAFA"
                                },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                    appearance: 'textfield',
                                },
                            }}
                        />
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button variant="outlined" sx={{ mr: 2 }} onClick={() => {
                        navigate('/SOorMDO/farmerCommitment')
                    }}>
                        cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={formik.isSubmitting}
                        onClick={async (e) => {
                            e.preventDefault();
                            // Validate form
                            const valid = await formik.validateForm();
                            formik.setTouched({
                                farmerName: true,
                                mobileNumber: true,
                                whatsappNumber: true,
                                districtName: true,
                                mandalName: true,
                                villageName: true,
                                geoCode: true,
                                totalAcres: true,
                                irrigationType: true,
                                irrigationAcres: true,
                                majorCrops: true,
                            });
                            if (Object.keys(valid).length === 0) {
                                const toNumber = (v) =>
                                    v === "" || v === null || v === undefined ? undefined : Number(v);
                                const districtValue = formik.values.districtId ? formik.values.districtId : formik.values.districtName.trim();
                                const mandalValue   = formik.values.mandalId   ? formik.values.mandalId   : formik.values.mandalName.trim();
                                const villageValue  = formik.values.villageId  ? formik.values.villageId  : formik.values.villageName.trim();
                                const finalMajorCrops = mergeMajorCropInput(formik.values.majorCrops, majorCropInput);
                                const payload = {
                                    name: formik.values.farmerName,
                                    mobile: formik.values.mobileNumber,
                                    whatsappNo: formik.values.whatsappNumber,
                                    districtId: districtValue,
                                    mandalId: mandalValue,
                                    villageId: villageValue,
                                    incharge_user_id: userId,
                                    geoCode: formik.values.geoCode,
                                    totalAcres: toNumber(formik.values.totalAcres),
                                    focusCrop: formik.values.focusCrop,
                                    irrigationType: formik.values.irrigationType,
                                    acresUnderIrrigation: formik.values.irrigationAcres === undefined || formik.values.irrigationAcres === null ? "" : String(formik.values.irrigationAcres),
                                    majorCrops: finalMajorCrops,
                                    existingUser: formik.values.existingUser,
                                };

                                if (majorCropInput.trim()) {
                                    formik.setFieldValue("majorCrops", finalMajorCrops, false);
                                    setMajorCropInput("");
                                }

                                if (Edit && id) {
                                    dispatch(updateFarmerCommitmentAction({
                                        endPoint: `farmer/${id}`,
                                        ...payload
                                    }));
                                } else {
                                    dispatch(createFarmerCommitmentAction(payload));
                                }
                            }
                        }}
                    >
                        Next
                    </Button>
                </Box>
            </form>

        </Paper>
    );
};

export default FarmerRegister;
